export interface PreviewCard {
  label: string;
  example: string;
  color: string;
}

type GoalId = "research" | "amazon" | "shopify" | "ads";

interface GoalTemplate {
  complaint: string;
  objection: string;
  wording: string;
  angle: string;
}

const GOAL_TEMPLATES: Record<GoalId, GoalTemplate> = {
  research: {
    complaint: `"Buyers often mention {product} losing effectiveness over time."`,
    objection: `"People hesitate because they can't tell if {product} actually works."`,
    wording: `"Finally found a {product} that does what I expected."`,
    angle: `"What 500+ buyers wish they'd known before buying {product}."`,
  },
  amazon: {
    complaint: `"Packaging and quality complaints keep coming up for {product}."`,
    objection: `"Shoppers question whether {product} reviews are genuine."`,
    wording: `"Matched the listing — exactly what I hoped {product} would be."`,
    angle: `"The #1 reason {product} gets returned, straight from reviews."`,
  },
  shopify: {
    complaint: `"Shipping delays and unclear tracking frustrate {product} buyers."`,
    objection: `"First-time visitors aren't sure they can trust this {product} brand."`,
    wording: `"Smooth experience — {product} matched expectations."`,
    angle: `"Trust-building messaging that converts more {product} visitors."`,
  },
  ads: {
    complaint: `"Buyers feel most {product} ads overpromise on results."`,
    objection: `"People scroll past because every {product} ad looks the same."`,
    wording: `"Saw the ad, tried {product} — pleasantly surprised."`,
    angle: `"A {product} hook backed by what real buyers actually say."`,
  },
};

const CARD_META: { key: keyof GoalTemplate; label: string; color: string }[] = [
  { key: "complaint", label: "Top buyer complaint", color: "text-red-400" },
  { key: "objection", label: "What stops the sale", color: "text-amber-400" },
  { key: "wording", label: "Buyer wording you can reuse", color: "text-sky-400" },
  { key: "angle", label: "Copy angle", color: "text-teal-400" },
];

/** Normalize product names: collapse spaces, fix "NAD +" → "NAD+" style patterns */
export function normalizeProductName(raw: string): string {
  let name = raw.trim().replace(/\s{2,}/g, " ");
  // Collapse space before +, -, & regardless of case (e.g. "Nad +" → "Nad+")
  name = name.replace(/(\w)\s+([+\-&])/g, "$1$2");
  // Uppercase short alpha tokens before + (common abbreviations: NAD+, NMN+, etc.)
  name = name.replace(/\b([A-Za-z]{2,4})\+/g, (_, w) => w.toUpperCase() + "+");
  return name || "your product";
}

export function buildOnboardingPreview(
  productName: string,
  selectedGoal: string | null,
): PreviewCard[] {
  const product = normalizeProductName(productName);
  const goalId = (selectedGoal as GoalId) || "research";
  const template = GOAL_TEMPLATES[goalId] ?? GOAL_TEMPLATES.research;

  return CARD_META.map(({ key, label, color }) => ({
    label,
    example: template[key].replace(/\{product\}/g, product),
    color,
  }));
}
